import { useCallback, useRef, useState } from 'react';
import {
  pushDevTranscriptSegment,
  resetSessionState,
} from '../api/mediatorService';
import { DEMO_SEGMENTS, SPEAKER_LABEL, type DemoSegment } from '../lib/demo-script';

interface Props {
  sessionId: string;
  accessToken: string;
  /** Playback speed multiplier. 1.0 = real-time, 1.5 = faster-than-live. */
  speed?: number;
}

type Status =
  | { kind: 'idle' }
  | { kind: 'playing'; index: number }
  | { kind: 'done' }
  | { kind: 'resetting' }
  | { kind: 'error'; message: string };

/**
 * Dev / demo control: streams the scripted mediation conversation into the
 * live session from inside the sidebar. Uses the same
 * `POST /dev/push-transcript` endpoint the terminal mock script hits, so
 * downstream (DO copilot, agreement generation) behavior is identical.
 *
 * Rendered as a low-key gray strip above the tab nav so it's always
 * accessible but visually recedes during a demo.
 */
export function DemoPlayer({ sessionId, accessToken, speed = 1.5 }: Props) {
  const [status, setStatus] = useState<Status>({ kind: 'idle' });
  const cancelRef = useRef(false);
  const timerRef = useRef<number | null>(null);

  const clearTimer = () => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const play = useCallback(
    async (opts: { skip?: boolean } = {}) => {
      cancelRef.current = false;
      clearTimer();

      let clock = 0;
      for (let i = 0; i < DEMO_SEGMENTS.length; i++) {
        if (cancelRef.current) return;
        const seg: DemoSegment = DEMO_SEGMENTS[i];
        clock += seg.waitAfterPrev;

        if (!opts.skip && seg.waitAfterPrev > 0) {
          setStatus({ kind: 'playing', index: i });
          await new Promise<void>((resolve) => {
            timerRef.current = window.setTimeout(
              resolve,
              (seg.waitAfterPrev * 1000) / speed,
            );
          });
          if (cancelRef.current) return;
        } else {
          setStatus({ kind: 'playing', index: i });
        }

        const start = clock;
        const end = clock + seg.duration;
        clock = end;

        try {
          await pushDevTranscriptSegment(
            sessionId,
            {
              speaker_label: SPEAKER_LABEL[seg.speaker],
              content: seg.text,
              start_time: Number(start.toFixed(2)),
              end_time: Number(end.toFixed(2)),
              confidence: 0.95,
            },
            accessToken,
          );
        } catch (err) {
          setStatus({
            kind: 'error',
            message: err instanceof Error ? err.message : 'Failed to push segment',
          });
          return;
        }
      }
      if (!cancelRef.current) setStatus({ kind: 'done' });
    },
    [sessionId, accessToken, speed],
  );

  const handlePlay = () => void play();
  const handleSkip = () => void play({ skip: true });
  const handleReset = async () => {
    cancelRef.current = true;
    clearTimer();
    setStatus({ kind: 'resetting' });
    try {
      await resetSessionState(sessionId, accessToken);
      setStatus({ kind: 'idle' });
    } catch (err) {
      setStatus({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Reset failed',
      });
    }
  };

  const progressLabel =
    status.kind === 'playing'
      ? `${status.index + 1}/${DEMO_SEGMENTS.length}`
      : status.kind === 'done'
        ? `done`
        : status.kind === 'resetting'
          ? 'resetting…'
          : status.kind === 'error'
            ? status.message
            : `idle`;

  const playing = status.kind === 'playing';
  const resetting = status.kind === 'resetting';

  const pillClass =
    'rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-gray-500 hover:bg-gray-200 hover:text-gray-800 disabled:cursor-not-allowed disabled:opacity-40';

  return (
    <div className="flex items-center justify-between gap-2 border-b border-gray-200 bg-gray-50 px-2 py-0.5 text-[10px] text-gray-400">
      <span className="uppercase tracking-wider">demo · {progressLabel}</span>
      <div className="flex items-center gap-0.5">
        <button type="button" className={pillClass} onClick={handlePlay} disabled={playing}>
          {playing ? '…' : 'play'}
        </button>
        <button type="button" className={pillClass} onClick={handleSkip} disabled={playing}>
          skip
        </button>
        <button
          type="button"
          className={pillClass}
          onClick={() => void handleReset()}
          disabled={resetting}
        >
          reset
        </button>
      </div>
    </div>
  );
}
